import { useQueries } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSession } from "@/api/auth-client";
import { api } from "@/api/client";
import { useDesks } from "@/api/hooks";
import type { SessionInfo } from "@/api/types";

interface NotificationPrefs {
  desktop: boolean;
}

interface AppActivityContextValue {
  prefs: NotificationPrefs;
  setDesktopEnabled: (enabled: boolean) => void;
  permission: NotificationPermission | "unsupported";
  requestPermission: () => Promise<NotificationPermission | "unsupported">;
  activeSessionCount: number;
  isBusy: boolean;
  resumeSessionPolling: () => void;
}

interface TrackedSession {
  deskId: string;
  deskName: string;
  session: SessionInfo;
}

const STORAGE_KEY = "rondel.notifications";
const DEFAULT_PREFS: NotificationPrefs = { desktop: false };
const DEFAULT_TITLE = "Rondel";
const BUSY_FAVICON_HREF = "/frogs/working.gif";
const READY_FAVICON_HREF = "/frogs/sleeping.gif";
const POLLING_GRACE_MS = 60_000;

const AppActivityContext = createContext<AppActivityContextValue | null>(null);

function loadPrefs(): NotificationPrefs {
  if (typeof window === "undefined") return DEFAULT_PREFS;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_PREFS;
    return { ...DEFAULT_PREFS, ...(JSON.parse(raw) as Partial<NotificationPrefs>) };
  } catch {
    return DEFAULT_PREFS;
  }
}

function savePrefs(prefs: NotificationPrefs) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
}

function sessionPreview(s: SessionInfo): string {
  const text = (s.lastMessage ?? "").replace(/\s+/g, " ").trim();
  if (text) return text.slice(0, 120);
  const model = s.model.split("/").pop() ?? s.model;
  return `${s.invocation} · ${model}`;
}

function isTransitionWorthNotifying(prev: string | undefined, next: string): boolean {
  return (prev === "starting" || prev === "running") && next === "completed";
}

function isSessionActive(session: SessionInfo): boolean {
  return session.agentStatus === "starting" || session.agentStatus === "running";
}

function setFavicon(href: string) {
  if (typeof document === "undefined") return;

  let link = document.querySelector<HTMLLinkElement>('link[data-rondel-favicon="true"]');
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    link.setAttribute("data-rondel-favicon", "true");
    document.head.appendChild(link);
  }
  link.href = href;
}

export function AppActivityProvider({ children }: { children: React.ReactNode }) {
  const { data: session } = useSession();
  const [prefs, setPrefs] = useState<NotificationPrefs>(() => loadPrefs());
  const [forcePolling, setForcePolling] = useState(false);
  const [pollWhileBusy, setPollWhileBusy] = useState(false);
  const permission: NotificationPermission | "unsupported" =
    typeof Notification === "undefined" ? "unsupported" : Notification.permission;
  const pollingTimeoutRef = useRef<number | null>(null);

  const setDesktopEnabled = useCallback((enabled: boolean) => {
    setPrefs((prev) => {
      const next = { ...prev, desktop: enabled };
      savePrefs(next);
      return next;
    });
  }, []);

  const requestPermission = useCallback(async () => {
    if (typeof Notification === "undefined") return "unsupported" as const;
    return Notification.requestPermission();
  }, []);

  const resumeSessionPolling = useCallback(() => {
    console.log("[app-activity] resumeSessionPolling");
    setForcePolling(true);
    if (pollingTimeoutRef.current != null) {
      window.clearTimeout(pollingTimeoutRef.current);
    }
    pollingTimeoutRef.current = window.setTimeout(() => {
      console.log("[app-activity] polling grace expired");
      setForcePolling(false);
      pollingTimeoutRef.current = null;
    }, POLLING_GRACE_MS);
  }, []);

  const authReady =
    !!session?.user &&
    (typeof window === "undefined" || window.location.pathname !== "/login");

  const { data } = useDesks({ enabled: authReady });
  const desks = authReady ? (data?.desks ?? []) : [];

  const shouldPollSessions = forcePolling || pollWhileBusy;

  const sessionQueries = useQueries({
    queries: desks.map((desk) => ({
      queryKey: ["sessions", desk.id],
      queryFn: async () => {
        console.log("[app-activity] listSessions query", {
          deskId: desk.id,
          shouldPollSessions,
          forcePolling,
          pollWhileBusy,
        });
        return api.listSessions(desk.id);
      },
      refetchInterval: shouldPollSessions ? 3_000 : false,
      refetchIntervalInBackground: true,
      enabled: authReady,
    })),
  });

  const trackedSessions = useMemo<TrackedSession[]>(() => {
    const all: TrackedSession[] = [];
    for (const [index, desk] of desks.entries()) {
      const sessions = sessionQueries[index]?.data ?? [];
      for (const session of sessions) {
        all.push({ deskId: desk.id, deskName: desk.name, session });
      }
    }
    return all;
  }, [desks, sessionQueries]);

  const activeSessionCount = useMemo(
    () => trackedSessions.filter(({ session }) => isSessionActive(session)).length,
    [trackedSessions],
  );
  const isBusy = activeSessionCount > 0;

  useEffect(() => {
    console.log("[app-activity] polling state", {
      desks: desks.length,
      trackedSessions: trackedSessions.length,
      activeSessionCount,
      isBusy,
      forcePolling,
      pollWhileBusy,
      shouldPollSessions,
      statuses: trackedSessions.map(({ deskId, session }) => ({
        deskId,
        sessionId: session.sessionId,
        status: session.agentStatus,
      })),
    });
  }, [
    desks.length,
    trackedSessions,
    activeSessionCount,
    isBusy,
    forcePolling,
    pollWhileBusy,
    shouldPollSessions,
  ]);

  const prevBusyRef = useRef(false);
  const prevStatusRef = useRef(new Map<string, string>());
  const lastNotifiedRef = useRef(new Map<string, string>());

  useEffect(() => {
    if (isBusy) {
      console.log("[app-activity] entering busy state");
      setPollWhileBusy(true);
    }
    if (prevBusyRef.current && !isBusy) {
      console.log("[app-activity] leaving busy state; stopping polling");
      setPollWhileBusy(false);
      setForcePolling(false);
      if (pollingTimeoutRef.current != null) {
        window.clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
    }
    prevBusyRef.current = isBusy;
  }, [isBusy]);

  useEffect(() => {
    return () => {
      if (pollingTimeoutRef.current != null) {
        window.clearTimeout(pollingTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const seen = new Set<string>();

    for (const tracked of trackedSessions) {
      const key = `${tracked.deskId}:${tracked.session.sessionId}`;
      seen.add(key);

      const prev = prevStatusRef.current.get(key);
      const next = tracked.session.agentStatus;

      if (prev === undefined) {
        prevStatusRef.current.set(key, next);
        continue;
      }

      if (!isTransitionWorthNotifying(prev, next)) {
        prevStatusRef.current.set(key, next);
        continue;
      }

      if (lastNotifiedRef.current.get(key) === next) {
        prevStatusRef.current.set(key, next);
        continue;
      }

      if (
        prefs.desktop &&
        permission === "granted" &&
        typeof document !== "undefined" &&
        document.visibilityState === "hidden"
      ) {
        const body = `${tracked.deskName} · ${sessionPreview(tracked.session)}`;
        try {
          const notification = new Notification("Task completed", {
            body,
            tag: key,
            requireInteraction: true,
          });
          notification.onclick = () => {
            window.focus();
            window.location.assign(`/desk/${tracked.deskId}`);
          };
        } catch {
          // Ignore notification delivery failures.
        }
      }

      lastNotifiedRef.current.set(key, next);
      prevStatusRef.current.set(key, next);
    }

    for (const key of prevStatusRef.current.keys()) {
      if (!seen.has(key)) {
        prevStatusRef.current.delete(key);
        lastNotifiedRef.current.delete(key);
      }
    }
  }, [trackedSessions, prefs.desktop, permission]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.title = isBusy ? `${DEFAULT_TITLE} (${activeSessionCount})` : DEFAULT_TITLE;
    setFavicon(isBusy ? BUSY_FAVICON_HREF : READY_FAVICON_HREF);
  }, [activeSessionCount, isBusy]);

  const value = useMemo<AppActivityContextValue>(
    () => ({
      prefs,
      setDesktopEnabled,
      permission,
      requestPermission,
      activeSessionCount,
      isBusy,
      resumeSessionPolling,
    }),
    [
      prefs,
      setDesktopEnabled,
      permission,
      requestPermission,
      activeSessionCount,
      isBusy,
      resumeSessionPolling,
    ],
  );

  return <AppActivityContext.Provider value={value}>{children}</AppActivityContext.Provider>;
}

export function useAppActivity() {
  const ctx = useContext(AppActivityContext);
  if (!ctx) {
    throw new Error("useAppActivity must be used within AppActivityProvider");
  }
  return ctx;
}

export function useNotificationPrefs() {
  return useAppActivity();
}
